import cloneDeep from 'lodash/cloneDeep';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import map from 'lodash/map';

import * as sharedTypes from 'src/actions/shared/sharedTypes';
import EmailDomain, { EmailDomainState } from 'src/models/Email/EmailDomain';
import createCollectionResource from 'src/actions/shared/createResource';
import validateType from 'src/adapters/validateType';
import { DomainError } from 'src/models/DomainError';
import { Unauthorized } from '@ad-scripts/application/lib/error';
import { logout } from 'src/actions/auth/authSlice';
import { toastError, toastSuccess, toastWarn } from 'src/actions/toast';

const emailDomainsResource = createCollectionResource<EmailDomain>({
  name: 'emailDomains',
  endpoint: 'v1/orgs/:orgId/email-domains/:id',
  type: EmailDomain,
  reducers: {
    verifyStarted(state, action) {
      state[action.payload.id] = 'loading';
    },
    verifySuccess(state, action) {
      const verifiedEmailDomain = action.payload as EmailDomain;

      const emailDomains: EmailDomain[] = cloneDeep(state.values);

      const verifiedEmailDomainIndex = findIndex(
        emailDomains,
        emailDomain => emailDomain.id === verifiedEmailDomain.id
      );

      if (verifiedEmailDomainIndex !== -1) {
        emailDomains[verifiedEmailDomainIndex] = { ...verifiedEmailDomain };
      }

      state.values = emailDomains;
      state[action.payload.id] = 'succeeded';
    },
    verifyRequestError(state, action) {
      state[action.payload.id] = 'failed';
    },
  },
});

const verify: sharedTypes.AppThunk =
  (emailDomain: EmailDomain) =>
  async (dispatch, _, { gateways }) => {
    const customerDomain = get(emailDomain, 'content.customerDomain', '');
    try {
      dispatch(emailDomainsResource.actions.verifyStarted(emailDomain));
      const result = (await gateways.emailDomains.verify({ accountId: emailDomain.id })) as EmailDomain;
      dispatch(emailDomainsResource.actions.verifySuccess(result));

      result.content.state === EmailDomainState.ACTIVE
        ? dispatch(toastSuccess(`"${customerDomain}" domain has been verified`))
        : dispatch(toastWarn(`"${customerDomain}" domain has not been verified`));
    } catch (err) {
      if (err instanceof Unauthorized) {
        dispatch(logout());
      } else {
        dispatch(emailDomainsResource.actions.verifyRequestError(emailDomain));
      }
      dispatch(toastError(`Unable to verify email domain "${customerDomain}". Try again later.`));
    }
  };

const copyToClipboard: sharedTypes.AppThunk = (value: string) => async dispatch => {
  try {
    if (!value) {
      return false;
    }
    await navigator.clipboard.writeText(value);
  } catch (e) {
    dispatch(toastError('Copy to clipboard failed'));
    return false;
  }
  return true;
};

const validate: sharedTypes.AppThunk = (attrs: any) => dispatch => {
  try {
    validateType(EmailDomain, attrs);
  } catch (err) {
    const translatedErrors = map((err as any).errors, err => translateError(err));
    dispatch(emailDomainsResource.actions.validationError(translatedErrors));
    return false;
  }
  return true;
};

const translateError = (err: DomainError) => {
  if (err.code === 'taken') {
    return { ...err, attr: 'content.customerDomain' };
  }
  return { ...err };
};

emailDomainsResource.effects.copyToClipboard = copyToClipboard;
emailDomainsResource.effects.validate = validate;
emailDomainsResource.effects.verify = verify;

export default emailDomainsResource;
